import { z } from "zod";

/**
 * Platform-wide default counts surfaced in the project model-routing settings
 * (`modelRouting.getPlatformDefaults`): how many inference.net-public providers
 * and models a team inherits before configuring any of its own.
 */
export const platformDefaultsSchema = z.object({
  platformModelCount: z.number(),
  platformProviderCount: z.number(),
});

/**
 * One provider row in the project model-routing settings list
 * (`modelRouting.listProviders`). Combines the stored provider with fields the
 * service derives per requesting team.
 *
 * `scope` is a service-derived classification, not a DB column: `platform` for
 * inference.net-public providers, `byok` for a team's bring-your-own-key child
 * of a platform provider, `custom` for a fully team-owned provider.
 */
export const modelProviderListItemSchema = z.object({
  additionalHeaders: z.record(z.string(), z.string()).nullable(),
  baseUrl: z.string(),
  createdAt: z.date(),
  format: z.string(),
  hasStoredApiKey: z.boolean(),
  isEditable: z.boolean(),
  maskedApiKey: z.string().nullable(),
  name: z.string(),
  parentProviderId: z.string().nullable(),
  priority: z.number(),
  providerId: z.string(),
  providerSlug: z.string().nullable(),
  scope: z.enum(["platform", "byok", "custom"]),
  supportsTeamApiKey: z.boolean(),
  teamId: z.string(),
  updatedAt: z.date(),
});

/** A team-scoped alias pointing at a model (`modelRouting.listModels`). */
const modelAliasSchema = z.object({
  alias: z.string(),
  teamId: z.string(),
});

/**
 * One provider route under a model in the settings list. The nine `cost*` fields
 * are decimal strings (nullable — a route need not price every dimension).
 * `messageFormat` mirrors the `ModelProviderRouteMessageFormat` union
 * (`@kuzco/db`): the `message_format` column is `text` but is constrained to
 * these values by application code on every write, and downstream consumers (the
 * web settings UI) rely on the narrow type — so it is a `z.enum`, not `z.string`.
 */
const modelRouteSchema = z.object({
  routeId: z.string(),
  modelId: z.string(),
  costCachedInputPerToken: z.string().nullable(),
  costCacheWrite1hInputPerToken: z.string().nullable(),
  costCacheWrite5mInputPerToken: z.string().nullable(),
  costInputPerToken: z.string().nullable(),
  costLongContextCachedInputPerToken: z.string().nullable(),
  costLongContextInputPerToken: z.string().nullable(),
  costLongContextOutputPerToken: z.string().nullable(),
  costOutputPerToken: z.string().nullable(),
  costReasoningPerToken: z.string().nullable(),
  downstreamAlias: z.string().nullable(),
  isCallable: z.boolean(),
  isBillable: z.boolean(),
  longContextThresholdInputTokens: z.number().nullable(),
  messageFormat: z.enum([
    "openai-chat-completions",
    "openai-responses",
    "openai-embeddings",
    "anthropic-messages",
    "gemini-content",
  ]),
  providerBaseUrl: z.string(),
  providerId: z.string(),
  providerName: z.string(),
  providerPriority: z.number(),
  providerSlug: z.string().nullable(),
  providerTeamId: z.string(),
});

/**
 * A model in the project model-routing settings list (`modelRouting.listModels`):
 * the model's capabilities/metadata plus its team aliases and provider routes.
 * `isEditable` is derived (owned by the requesting team).
 */
export const modelSettingsItemSchema = z.object({
  aliases: z.array(modelAliasSchema),
  routes: z.array(modelRouteSchema),
  canonicalAlias: z.string(),
  creator: z.string(),
  id: z.string(),
  inputModalities: z.array(z.string()),
  isEditable: z.boolean(),
  isJudgeModel: z.boolean(),
  isTrainable: z.boolean(),
  maxContextSize: z.number(),
  maxOutputTokens: z.number(),
  name: z.string(),
  outputModalities: z.array(z.string()),
  ownerTeamId: z.string(),
  parameters: z.string().nullable(),
  quantization: z.string().nullable(),
  reasoningSupported: z.boolean(),
  responseJsonObjectSupported: z.boolean(),
  responseStructuredOutputsSupported: z.boolean(),
  toolsSupported: z.boolean(),
});
